
import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ReCAPTCHA from 'react-google-recaptcha';
import { validatePassword } from '@/utils/passwordUtils';

interface EmailAuthFormProps {
  isSignUp: boolean;
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  captchaToken: string | null;
  setCaptchaToken: (token: string | null) => void;
}

// Password strength colors
const strengthColors = {
  weak: "bg-red-500",
  medium: "bg-yellow-500",
  strong: "bg-green-500",
};

const EmailAuthForm: React.FC<EmailAuthFormProps> = ({
  isSignUp,
  email,
  setEmail,
  password,
  setPassword,
  loading,
  onSubmit,
  captchaToken,
  setCaptchaToken
}) => {
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    color: "",
    message: ""
  });

  // Recaptcha configuration
  const recaptchaKey = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // Test key

  // Update password strength indicator
  useEffect(() => {
    if (!password || password.length === 0) {
      setPasswordStrength({ score: 0, color: "", message: "" });
      return;
    }

    // Simple password strength calculation
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Get validation message
    const errorMessage = validatePassword(password);

    let color = "";
    let message = "";

    if (errorMessage) {
      color = strengthColors.weak;
      message = errorMessage;
    } else if (score <= 2) {
      color = strengthColors.weak;
      message = "Weak password";
    } else if (score === 3) {
      color = strengthColors.medium;
      message = "Decent password";
    } else {
      color = strengthColors.strong;
      message = "Strong password";
    }

    setPasswordStrength({ score, color, message });
  }, [password]);

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="w-full"
          autoComplete={isSignUp ? "username" : "email"}
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="password" className="block text-sm font-medium">
          Password
        </label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          autoComplete={isSignUp ? "new-password" : "current-password"}
          className="w-full"
        />

        {/* Password strength indicator - only shown during signup */}
        {isSignUp && password && (
          <div className="mt-1">
            <div className="h-1 w-full bg-gray-200 rounded-full">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${passwordStrength.color}`}
                style={{ width: `${passwordStrength.score * 25}%` }}
              />
            </div>
            <p className={`text-xs mt-1 ${passwordStrength.color ? `text-${passwordStrength.color.split('-')[1]}-600` : ''}`}>
              {passwordStrength.message}
            </p>
          </div>
        )}
      </div>
      
      {isSignUp && (
        <div className="pt-2">
          <ReCAPTCHA
            sitekey={recaptchaKey}
            onChange={setCaptchaToken}
            className="transform scale-90 origin-left"
          />
        </div>
      )}
      
      <Button 
        type="submit" 
        className="w-full"
        disabled={loading || (isSignUp && !captchaToken)}
      >
        {loading ? (
          <span>Loading...</span>
        ) : (
          <span>{isSignUp ? 'Sign Up' : 'Sign In'}</span>
        )}
      </Button>
    </form>
  );
};

export default EmailAuthForm;
